import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, FileText, Check, Loader2, Edit3, RefreshCw } from "lucide-react";
import { AIOptionsModalProps, AIOption, Template, Notes } from "./interfaces";
import { notification } from "antd";
import { templateService } from "../../services/templateService";
import { speechService } from "../../services/speechService";
import { SmartEditModal } from "./SmartEditModal";

export const AIOptionsModal: React.FC<AIOptionsModalProps> = ({
  isOpen,
  onClose,
  selectedRecordingId,
  patientName,
  selectedVisitType,
  selectedPronouns,
  selectedNoteLength,
  setNotes,
  onError,
}) => {
  const [selectedOption, setSelectedOption] = useState<AIOption | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isSmartEditModalOpen, setIsSmartEditModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null
  );
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const fetchedTemplates = await templateService.getTemplates();
        setTemplates(fetchedTemplates);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const aiOptions: AIOption[] = [
    {
      id: "smart-edit",
      title: "Smart Edit",
      description:
        "Add custom instructions, context, and formatting rules to regenerate your Scribe",
      icon: <Edit3 className="w-5 h-5" />,
      action: () => setIsSmartEditModalOpen(true),
    },
    {
      id: "change-template",
      title: "Change Template",
      description:
        "Select a different template to regenerate your Scribe with a new format",
      icon: <FileText className="w-5 h-5" />,
      action: () => setIsTemplateModalOpen(true),
    },
    {
      id: "reprocess",
      title: "Reprocess Recording",
      description:
        "Regenerate your Scribe with the current settings and template",
      icon: <RefreshCw className="w-5 h-5" />,
      action: async () => {
        setIsLoading(true);
        try {
          // Get current template ID (fallback to default if not found)
          const templateId = "1"; // Default template for reprocess

          // Call the retry-process API with current options
          await speechService.retryProcess(
            selectedRecordingId!,
            templateId,
            {
              patient_name: patientName,
              visit_type: selectedVisitType,
              pronouns: selectedPronouns,
              note_length: selectedNoteLength,
            }
          );
          
          // Show success message
          notification.success({
            message: "Reprocessing started successfully!",
            description: "Your recording is being processed again.",
            placement: "topRight",
            duration: 4,
          });
          
          // Close modal
          onClose();
          
          // Refresh the page to show updated content after a short delay
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          
        } catch (error) {
          console.error("Error reprocessing:", error);
          onError("Failed to start reprocessing");
        } finally {
          setIsLoading(false);
        }
      },
    },
  ];

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-[#00000040] backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-lg sm:max-w-xl max-h-[95vh] sm:max-h-[85vh] overflow-hidden"
        >
          <div className="p-4 sm:p-6 border-b border-[#F2F2F7]">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-[#1D1D1F]">
                  AI Suggestions
                </h2>
                <p className="text-sm text-[#86868B] mt-1">
                  Choose an action to enhance your Scribe
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-[#F5F5F7] rounded-full transition-colors flex-shrink-0 ml-4"
              >
                <X className="w-5 h-5 text-[#86868B]" />
              </button>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              {aiOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={option.action}
                  disabled={isLoading}
                  className="w-full p-4 bg-white hover:bg-[#F5F5F7] border border-[#E5E5EA] rounded-xl transition-all duration-200 group hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-[#0071E3]/10 rounded-xl text-[#0071E3] group-hover:bg-[#0071E3]/20 transition-colors flex-shrink-0">
                      {option.icon}
                    </div>
                    <div className="text-left">
                      <h3 className="font-medium text-[#1D1D1F] text-sm sm:text-base">
                        {option.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-[#86868B] mt-1">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Template Change Modal */}
      {isTemplateModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-[#00000040] backdrop-blur-sm z-[60] flex items-center justify-center"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          >
            <div className="p-4 sm:p-6 border-b border-[#F2F2F7]">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl sm:text-2xl font-semibold text-[#1D1D1F]">
                    Change Template
                  </h2>
                  <p className="text-sm text-[#86868B] mt-1">
                    Select template and regenerate your Scribe. Your Scribe will
                    be processed again.
                  </p>
                </div>
                <button
                  onClick={() => setIsTemplateModalOpen(false)}
                  className="p-2 hover:bg-[#F5F5F7] rounded-full transition-colors flex-shrink-0 ml-4"
                >
                  <X className="w-5 h-5 text-[#86868B]" />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 max-h-[50vh] overflow-y-auto">
              <div className="space-y-3 sm:space-y-4">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    disabled={isLoading}
                    className={`w-full p-4 border rounded-xl transition-all duration-200 ${
                      selectedTemplate?.id === template.id
                        ? "border-[#0071E3] bg-[#0071E3]/10"
                        : "border-[#E5E5EA] hover:bg-[#F5F5F7] hover:shadow-sm"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="text-left">
                        <h3 className="font-medium text-[#1D1D1F] text-sm sm:text-base">
                          {template.name}
                        </h3>
                        {template.description && (
                          <p className="text-xs sm:text-sm text-[#86868B] mt-1">
                            {template.description}
                          </p>
                        )}
                      </div>
                      {selectedTemplate?.id === template.id && (
                        <Check className="w-5 h-5 text-[#0071E3] flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-[#F2F2F7] flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setIsTemplateModalOpen(false)}
                className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium text-[#1D1D1F] border border-[#E5E5EA] hover:bg-[#F5F5F7] rounded-xl transition-all duration-200 hover:shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedTemplate) return;
                  setIsLoading(true);
                  try {
                    // Use the retry-process API instead of generateNotes
                    await speechService.retryProcess(
                      selectedRecordingId!,
                      selectedTemplate.id.toString(),
                      {
                        patient_name: patientName,
                        visit_type: selectedVisitType,
                        pronouns: selectedPronouns,
                        note_length: selectedNoteLength,
                      }
                    );
                    
                    // Show success message
                    notification.success({
                      message: "Reprocessing started successfully!",
                      description: "Your recording is being processed with the new template.",
                      placement: "topRight",
                      duration: 4,
                    });
                    
                    // Close modals
                    setIsTemplateModalOpen(false);
                    onClose();
                    
                    // Refresh the page to show updated content after a short delay
                    setTimeout(() => {
                      window.location.reload();
                    }, 2000);
                    
                  } catch (error) {
                    console.error("Error starting reprocess:", error);
                    onError("Failed to start reprocessing");
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={!selectedTemplate || isLoading}
                className="inline-flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 rounded-xl transition-all duration-200 shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                    <span>Regenerating...</span>
                  </>
                ) : (
                  <span>Regenerate</span>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Smart Edit Modal */}
      {isSmartEditModalOpen && (
        <SmartEditModal
          isOpen={isSmartEditModalOpen}
          onClose={() => setIsSmartEditModalOpen(false)}
          selectedRecordingId={selectedRecordingId!}
          currentOptions={{
            patient_name: patientName,
            visit_type: selectedVisitType,
            pronouns: selectedPronouns,
            note_length: selectedNoteLength,
            template_id: "1", // Default template ID
          }}
          onReprocessSuccess={(formData) => {
            
            // Close the AI Options modal
            onClose();
            
            // Refresh the page after a short delay
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }}
          onError={onError}
        />
      )}
    </>
  );
}; 